import type { AppContext } from "../app/app";
import type { Dimension, Person, Tag, Valence } from "../data/model";
import { RATING_MAX } from "../data/rating";
import type { Standing } from "../data/standing";
import { sharpen } from "../llm/partner";
import { el, pct } from "./dom";
import { requireProcessingConsent } from "./privacy";
import { maybeRequestPersistence } from "./storage";
import { renderEventForm } from "./event-form";

const fmt1 = (n: number) => n.toFixed(1);
const shortDate = (ms: number) => new Date(ms).toLocaleDateString("en-GB");

// A unified log entry: a read (baseline / re-rate) or a dated event.
export type LogEntry =
  | { kind: "read"; id: string; at: number; rating: number; from: number | null; note: string }
  | { kind: "event"; id: string; at: number; valence: Valence; magnitude: number; note: string };

const toggle = (e: HTMLElement) =>
  (e.style.display = e.style.display === "none" ? "block" : "none");

// Name with its one-line definition beneath, so a rating is calibrated against
// a shared sense of the quality (definition shows only when set).
function nameCell(dim: Dimension): HTMLElement {
  return el(
    "div",
    { class: "dim-row__namecell" },
    el("span", { class: "dim-row__name", text: dim.name }),
    dim.definition ? el("span", { class: "dim-row__def", text: dim.definition }) : null,
  );
}

// One quality row: the evidence-updated score on top (read-only), with actions
// to re-rate, log an event, or open the log. No persistent 1..5 dial.
export function renderDimRow(
  ctx: AppContext,
  person: Person,
  dim: Dimension,
  tag: Tag | undefined,
  standing: Standing | null,
  share: number,
  log: LogEntry[],
): HTMLElement {
  const refresh = () => ctx.refresh();

  if (!tag || !standing) {
    const picker = readPicker(ctx, person, dim, 0, refresh);
    picker.style.display = "none";
    const setBtn = el("button", {
      class: "inst__open",
      text: "Set your rating",
      on: {
        click: () => {
          picker.style.display = "block";
          setBtn.style.display = "none";
        },
      },
    });
    return el(
      "div",
      { class: "dim-row" },
      el(
        "div",
        { class: "dim-row__head" },
        nameCell(dim),
        el("span", { class: "dim-row__share num", text: "not rated" }),
      ),
      el("div", { class: "dim-row__actions" }, setBtn),
      picker,
    );
  }

  const band = standing.band;
  const head = el(
    "div",
    { class: "dim-row__head" },
    nameCell(dim),
    el(
      "span",
      { class: "dim-row__score num" },
      el("span", { class: "dim-row__point", text: fmt1(standing.point) }),
      el("span", { class: "dim-row__sharepct", text: ` · ${pct(share)} share` }),
    ),
  );
  // Provenance line in plain English: the big score above is your 1..5 rating
  // adjusted by the events you logged. Name both numbers and the cause so the
  // relationship is explicit (this line only shows once events exist).
  const moved = fmt1(standing.point) !== fmt1(standing.read);
  const evs = `${standing.eventCount} event${standing.eventCount === 1 ? "" : "s"}`;
  const lead = moved
    ? `your rating ${standing.read}, now ${fmt1(standing.point)} after ${evs}`
    : `your rating ${standing.read}, unchanged after ${evs}`;
  const bandLine = band
    ? el("div", {
        class: "dim-row__band",
        text: `${lead} · indicative reliability ${fmt1(band.low)}–${fmt1(band.high)}`,
      })
    : null;

  const picker = readPicker(ctx, person, dim, tag.rating, refresh);
  picker.style.display = "none";
  const eventForm = renderEventForm(ctx, person.id, dim, refresh);
  eventForm.style.display = "none";
  const logBody = renderLog(ctx, log);
  logBody.style.display = "none";

  // Primary actions grouped left; the destructive Clear sits apart on the right.
  const actions = el(
    "div",
    { class: "dim-row__actions" },
    el(
      "div",
      { class: "dim-row__actions-main" },
      el("button", { class: "inst__open", text: "Re-rate", on: { click: () => toggle(picker) } }),
      el("button", { class: "inst__open", text: "Log event", on: { click: () => toggle(eventForm) } }),
      el("button", { class: "inst__open", text: `History (${log.length})`, on: { click: () => toggle(logBody) } }),
    ),
    el("button", {
      class: "inst__open dim-row__clear",
      text: "Clear",
      on: { click: async () => { await ctx.repo.deleteTag(tag.id); refresh(); } },
    }),
  );

  return el("div", { class: "dim-row" }, head, bandLine, actions, picker, eventForm, logBody);
}

function readPicker(
  ctx: AppContext,
  person: Person,
  dim: Dimension,
  current: number,
  refresh: () => void,
): HTMLElement {
  let selected = current;
  const segs: HTMLButtonElement[] = [];
  const paint = () => segs.forEach((b, i) => b.classList.toggle("is-on", i < selected));
  const rating = el("div", {
    class: "rating",
    attrs: { role: "group", "aria-label": `Rating of ${dim.name}, 1 to ${RATING_MAX}` },
  });
  for (let n = 1; n <= RATING_MAX; n++) {
    const s = el("button", {
      class: "rating__seg",
      type: "button",
      text: String(n),
      on: { click: () => { selected = n; paint(); } },
    });
    segs.push(s);
    rating.append(s);
  }
  paint();

  const note = el("input", {
    class: "input rationale-input",
    type: "text",
    placeholder: "Note (optional): what informs this rating?",
  });
  const sensitiveNote = el("p", {
    class: "sensitive-note",
    text: "Do not record sensitive personal data (health, politics, religion, sexuality, etc.).",
  });
  const err = el("span", { class: "error" });
  const set = el("button", {
    class: "btn btn--primary",
    text: "Set rating",
    on: {
      click: async () => {
        if (selected < 1) {
          err.textContent = `Pick a rating from 1 to ${RATING_MAX}.`;
          return;
        }
        await ctx.repo.upsertTag({
          person_id: person.id,
          dimension_id: dim.id,
          rating: selected,
          rationale: note.value.trim(),
        });
        // First commitment: ask the browser to keep the data persistently.
        void maybeRequestPersistence(ctx);
        refresh();
      },
    },
  });

  const question = el("p", { class: "sharpen__q" });
  question.style.display = "none";
  const sharpenBtn = el("button", {
    class: "btn btn--ghost",
    text: "Sharpen",
    on: {
      click: async () => {
        if (!(await requireProcessingConsent(ctx))) return;
        sharpenBtn.disabled = true;
        question.style.display = "block";
        question.textContent = "Thinking...";
        try {
          question.textContent = await sharpen(ctx.provider, person.display_name, dim.name, note.value);
        } catch (e) {
          question.textContent = e instanceof Error ? e.message : "Could not reach the model.";
        } finally {
          sharpenBtn.disabled = false;
        }
      },
    },
  });

  return el(
    "div",
    { class: "read-picker" },
    el("p", {
      class: "inst__form-intro",
      text: `Your rating of ${dim.name}, 1 (low) to 5 (high). Re-rating resets the baseline.`,
    }),
    rating,
    el("div", { class: "dim-row__controls" }, note, set, sharpenBtn),
    sensitiveNote,
    el("div", { class: "dim-row__foot" }, err),
    question,
  );
}

function renderLog(ctx: AppContext, log: LogEntry[]): HTMLElement {
  const wrap = el("div", { class: "log" });
  if (log.length === 0) {
    wrap.append(el("div", { class: "history__row", text: "No log yet." }));
    return wrap;
  }
  for (const e of log) {
    if (e.kind === "read") {
      wrap.append(
        el(
          "div",
          { class: "history__row" },
          el("span", { class: "history__date num", text: shortDate(e.at) }),
          el("span", { class: "log__kind", text: "rating" }),
          el("span", {
            class: "history__step num",
            text: e.from !== null ? `${e.from} → ${e.rating}` : `set ${e.rating}`,
          }),
          e.note ? el("span", { class: "history__why", text: e.note }) : null,
          el("button", {
            class: "history__del",
            text: "Remove",
            on: { click: async () => { await ctx.repo.deleteRatingEvent(e.id); ctx.refresh(); } },
          }),
        ),
      );
    } else {
      wrap.append(
        el(
          "div",
          { class: "history__row" },
          el("span", { class: "history__date num", text: shortDate(e.at) }),
          el("span", {
            class: `log__kind log__kind--${e.valence === "+" ? "pos" : "neg"}`,
            text: "event",
          }),
          el("span", {
            class: `inst__sign inst__sign--${e.valence === "+" ? "pos" : "neg"} num`,
            text: `${e.valence}${e.magnitude}`,
          }),
          e.note ? el("span", { class: "history__why", text: e.note }) : null,
          el("button", {
            class: "history__del",
            text: "Remove",
            on: { click: async () => { await ctx.repo.deleteInstance(e.id); ctx.refresh(); } },
          }),
        ),
      );
    }
  }
  return wrap;
}
